// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Every pixel of chrome that is not the tower: the menu, the HUD, the build tray, the overlays and
// the results.
//
// ── THE DISMISSAL CONTRACT ──────────────────────────────────────────────────────────────────────
// `openSheet` gives every panel in this game all four ways out — a ≥44px close control, a tap or
// click anywhere on the backdrop, Escape, and a backdrop that is genuinely reachable. Not a choice of
// them, all four. A panel with one way out is a game you cannot play, and a sibling site shipped with
// its close button, its backdrop handler and its Escape key all firing correctly and was still
// impossible to dismiss, because the stylesheet's `display` beat the `hidden` attribute. The
// `[hidden] { display: none !important; }` rule in main.css is the other half of this, and
// `tests/layout.test.ts` pins it.

import { MODES, MODE_IDS, type ModeId } from './modes';
import { SPEED_LABELS, UNITS, phaseAt, type UnitKind } from './tuning';
import type { Sim } from './sim';
import type { Overlay } from './render';

export type ToolId = UnitKind | 'riser' | 'stair' | 'car' | 'sell';

export interface Els {
  app: HTMLElement;
  menu: HTMLElement;
  game: HTMLElement;
  results: HTMLElement;
  stage: HTMLElement;
  canvas: HTMLCanvasElement;
  cash: HTMLElement;
  rating: HTMLElement;
  shift: HTMLElement;
  speed: HTMLButtonElement;
  rail: HTMLElement;
  tools: HTMLElement;
  context: HTMLElement;
  banner: HTMLElement;
  bannerText: HTMLElement;
  bannerSkip: HTMLButtonElement;
  sheet: HTMLElement;
  sheetBody: HTMLElement;
  sheetClose: HTMLButtonElement;
  modeList: HTMLElement;
  resultsBody: HTMLElement;
}

const TOOLS: ReadonlyArray<{ id: ToolId; label: string; hint: string }> = [
  { id: 'office', label: 'Office', hint: 'Four workers. The tower’s bread and butter.' },
  { id: 'flat', label: 'Flat', hint: 'Two residents, travelling against the office peak.' },
  { id: 'shop', label: 'Shop', hint: 'Earns from passing footfall, not from rent.' },
  { id: 'service', label: 'Service', hint: 'No income. Grants one more car slot.' },
  { id: 'riser', label: 'Riser', hint: 'A new shaft. Eats two columns on every storey it passes.' },
  { id: 'stair', label: 'Stairs', hint: 'Links two storeys. Slow, cheap, better than nothing.' },
  { id: 'car', label: 'Car', hint: 'Tap a riser to add a car to it.' },
  { id: 'sell', label: 'Sell', hint: 'Empty units go at once; occupied ones are given notice.' },
];

export function build(root: HTMLElement): Els {
  root.innerHTML = `
    <main class="main-content">
      <section id="menu" class="screen">
        <h1 class="title">Riser</h1>
        <p class="tag">Run the tower. The rent is the easy part — it is the lifts that kill you.</p>
        <div id="modeList" class="modes"></div>
        <div class="menu-actions">
          <button id="play" class="btn primary">Play</button>
          <button id="howto" class="btn">How to play</button>
          <button id="about" class="btn">About</button>
          <button id="mute" class="btn" aria-pressed="false">Sound on</button>
        </div>
      </section>

      <section id="game" class="screen" hidden>
        <header class="hud">
          <span id="cash" class="hud-cash">£0</span>
          <span id="rating" class="hud-rating">0</span>
          <span id="shift" class="hud-shift" aria-label="shift progress"></span>
          <button id="speed" class="btn small" aria-label="game speed">×3</button>
          <button id="quit" class="btn small" aria-label="back to menu">Menu</button>
        </header>
        <div class="stage" id="stage"><canvas id="cv"></canvas></div>
        <div class="rail" id="rail" role="tablist">
          <button class="pill" data-ov="lift" role="tab" aria-selected="true">Lifts</button>
          <button class="pill" data-ov="mood" role="tab" aria-selected="false">Mood</button>
          <button class="pill" data-ov="money" role="tab" aria-selected="false">Money</button>
        </div>
        <div class="banner" id="banner" hidden>
          <span id="bannerText"></span>
          <button id="bannerSkip" class="btn small">Skip</button>
        </div>
        <div class="tray">
          <div class="tools" id="tools"></div>
          <div class="context" id="context">Tap a crowd to send a car to it.</div>
        </div>
      </section>

      <section id="results" class="screen" hidden>
        <div id="resultsBody"></div>
      </section>
    </main>

    <footer class="site-footer">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
    </footer>

    <div class="overlay" id="sheet" hidden>
      <div class="backdrop" id="backdrop"></div>
      <div class="panel" role="dialog" aria-modal="true">
        <button class="sheet-close" id="sheetClose" aria-label="Close">✕</button>
        <div class="sheet-body" id="sheetBody"></div>
      </div>
    </div>`;

  const q = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;
  const els: Els = {
    app: root,
    menu: q('menu'),
    game: q('game'),
    results: q('results'),
    stage: q('stage'),
    canvas: q<HTMLCanvasElement>('cv'),
    cash: q('cash'),
    rating: q('rating'),
    shift: q('shift'),
    speed: q<HTMLButtonElement>('speed'),
    rail: q('rail'),
    tools: q('tools'),
    context: q('context'),
    banner: q('banner'),
    bannerText: q('bannerText'),
    bannerSkip: q<HTMLButtonElement>('bannerSkip'),
    sheet: q('sheet'),
    sheetBody: q('sheetBody'),
    sheetClose: q<HTMLButtonElement>('sheetClose'),
    modeList: q('modeList'),
    resultsBody: q('resultsBody'),
  };

  for (const m of MODE_IDS) {
    const b = document.createElement('button');
    b.className = 'mode';
    b.dataset.mode = m;
    b.innerHTML = `<strong>${MODES[m].name}</strong><span>${MODES[m].blurb}</span>`;
    els.modeList.appendChild(b);
  }

  for (const t of TOOLS) {
    const b = document.createElement('button');
    b.className = 'tool';
    b.dataset.tool = t.id;
    b.type = 'button';
    b.innerHTML = `<span class="tool-name">${t.label}</span><span class="tool-cost">${costLabel(t.id)}</span>`;
    b.title = t.hint;
    els.tools.appendChild(b);
  }

  return els;
}

function costLabel(id: ToolId): string {
  if (id === 'sell') return '';
  if (id === 'riser') return 'from £810';
  if (id === 'stair') return '£90';
  if (id === 'car') return '£1600';
  return `£${UNITS[id as UnitKind].cost}`;
}

export const toolHint = (id: ToolId): string => TOOLS.find((t) => t.id === id)?.hint ?? '';

export function setMode(els: Els, mode: ModeId): void {
  for (const b of els.modeList.querySelectorAll<HTMLElement>('.mode')) {
    b.classList.toggle('on', b.dataset.mode === mode);
  }
}

export function setOverlay(els: Els, ov: Overlay): void {
  for (const b of els.rail.querySelectorAll<HTMLElement>('.pill')) {
    const on = b.dataset.ov === ov;
    b.classList.toggle('on', on);
    b.setAttribute('aria-selected', String(on));
  }
}

export function setTool(els: Els, tool: ToolId | null): void {
  for (const b of els.tools.querySelectorAll<HTMLElement>('.tool')) {
    b.classList.toggle('on', b.dataset.tool === tool);
  }
}

export function updateHud(els: Els, sim: Sim, speed: number): void {
  els.cash.textContent = `£${Math.round(sim.cash).toLocaleString('en-GB')}`;
  els.cash.classList.toggle('bad', sim.cash < 0);
  els.rating.textContent = `${sim.rating} · ${sim.population()}p`;
  els.speed.textContent = SPEED_LABELS[speed];
  const frac = sim.tick / 3600;
  els.shift.style.setProperty('--p', `${Math.round(frac * 100)}%`);
  els.shift.textContent = `Day ${Math.min(sim.day, sim.mode.days)}/${sim.mode.days} · ${phaseAt(sim.tick)}`;
}

/** The unit card. Everything a player needs to answer "why is this one unhappy". */
export function unitCard(sim: Sim, unitId: number): string {
  const u = sim.tower.units.find((x) => x.id === unitId);
  if (!u) return '<p>Gone.</p>';
  const spec = UNITS[u.kind];
  const wait = sim.landingWait(u.storey);
  const rows: string[] = [
    `<h2>${spec.name} · storey ${u.storey}</h2>`,
    `<dl>`,
    `<dt>Status</dt><dd>${u.notice ? 'Giving notice — gone tomorrow' : u.let ? 'Let' : 'Empty'}</dd>`,
  ];
  if (u.let) {
    rows.push(`<dt>People</dt><dd>${u.pop}</dd>`);
    rows.push(`<dt>Mood</dt><dd>${Math.round(u.sat * 100)}%</dd>`);
    rows.push(`<dt>Trips today</dt><dd>${u.tripsToday} (${u.lateTripsToday} slow)</dd>`);
    if (u.kind === 'shop') rows.push(`<dt>Footfall</dt><dd>${Math.round(u.footfallToday)} yesterday</dd>`);
    else rows.push(`<dt>Rent</dt><dd>£${Math.round(spec.rent * (1 + 0.05 * u.storey))}/day before mood</dd>`);
  } else {
    rows.push(`<dt>Empty for</dt><dd>${u.vacantDays} day${u.vacantDays === 1 ? '' : 's'}</dd>`);
  }
  rows.push(`<dt>Upkeep</dt><dd>£${spec.upkeep}/day</dd>`);
  rows.push(
    `<dt>Its landing</dt><dd>${Number.isFinite(wait) ? `${wait.toFixed(0)}s typical wait` : 'no lift reaches this storey'}</dd>`,
  );
  rows.push('</dl>');
  if (!u.let && Number.isFinite(wait) && wait > 40) {
    rows.push('<p class="note">Nobody will take this unit while its landing is this slow.</p>');
  }
  if (!u.let && !Number.isFinite(wait)) {
    rows.push('<p class="note">No riser stops here. Extend one, or change its policy.</p>');
  }
  return rows.join('');
}

/** The riser card, which is where the policy levers live. */
export function riserCard(sim: Sim, riserId: number): string {
  const r = sim.tower.risers.find((x) => x.id === riserId);
  if (!r) return '<p>Gone.</p>';
  const cars = sim.cars.filter((c) => c.riser === r.id).length;
  return `
    <h2>Riser ${String.fromCharCode(65 + r.id)}</h2>
    <dl>
      <dt>Reaches</dt><dd>concourse to storey ${r.hi}</dd>
      <dt>Cars</dt><dd>${cars}</dd>
      <dt>Serving</dt><dd>${r.policy === 'express' ? `concourse, then storey ${r.expressFrom} and up` : r.policy === 'down' ? 'every storey, answering down calls first' : 'every storey'}</dd>
    </dl>
    <p class="note">Express is faster for the top of the building and abandons everything below its floor.</p>
    <div class="row" data-riser="${r.id}">
      <button class="btn small" data-policy="all">All storeys</button>
      <button class="btn small" data-policy="express">Express</button>
      <button class="btn small" data-policy="down">Down-first</button>
    </div>
    <div class="row" data-riser="${r.id}">
      <button class="btn small" data-extend="3">Extend +3 storeys</button>
      <button class="btn small" data-addcar="1">Add a car · £1600</button>
    </div>`;
}

export const HOW_TO_PLAY = `
  <h2>How to play</h2>
  <p>Let floors to tenants and get them where they are going. Every person in the tower walks,
  queues and rides — and a tenant kept waiting too often gives notice and leaves.</p>
  <p><strong>Tap a waiting crowd</strong> to turn the nearest empty car around and send it there.
  The automatic controller finishes its sweep no matter who is standing where; you do not have to.</p>
  <p>Build from the tray. Every riser eats <strong>two columns on every storey it passes</strong>,
  so the lift you need costs you the floors you wanted.</p>
  <p class="note">The strip down the left edge is your building, one bar per storey. Long and red
  means somebody down there has been waiting a long time.</p>`;

export const ABOUT = `
  <h2>About</h2>
  <p>Riser is a tower-management game built for a phone. You are the building: the floors you let,
  the shafts you cut, and the cars you run in them.</p>
  <p>Everything on screen is simulated — every dot is a person with somewhere to be, and every pound
  of rent is paid because somebody actually completed a journey. Nothing is estimated.</p>
  <p>It runs entirely in your browser. No accounts, no cookies, no data leaves this page. Anonymous,
  cookie-less page-view counts come from Cloudflare Web Analytics.</p>
  <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.</p>`;

export function results(sim: Sim): string {
  const kept = sim.tower.units.filter((u) => u.let).length;
  const rows = sim.tower.units
    .filter((u) => u.tripsToday + u.lateTripsToday > 0 || u.let || u.notice)
    .sort((a, b) => a.sat - b.sat)
    .slice(0, 8)
    .map(
      (u) =>
        `<tr><td>${UNITS[u.kind].name} ${u.storey}</td><td>${u.let ? `${Math.round(u.sat * 100)}%` : 'empty'}</td><td>${u.lateTripsToday}</td></tr>`,
    )
    .join('');
  const worst = sim.stats.worstStorey;
  return `
    <h2>${sim.won ? 'The building runs' : 'Not this time'}</h2>
    <p class="ending">${sim.ending}</p>
    <div class="score">
      <div><span>${sim.rating}</span><label>${sim.mode.scoreLabel}</label></div>
      <div><span>${sim.population()}</span><label>people</label></div>
      <div><span>${Math.round(sim.stats.personSecondsLost / 3600)}</span><label>person-hours queued</label></div>
    </div>
    <p>Target: ${sim.goalText()}</p>
    <p>${sim.stats.trips} journeys made, ${sim.stats.giveUps} people gave up and walked out,
    ${sim.stats.leaves} tenancies left. ${kept} units still let.
    The worst storey was <strong>${worst}</strong>, averaging ${sim.stats.worstStoreyWaitS.toFixed(0)}s a wait.</p>
    <table class="board"><thead><tr><th>Unit</th><th>Mood</th><th>Slow trips</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="menu-actions">
      <button id="again" class="btn primary">Play again</button>
      <button id="share" class="btn">Share this tower</button>
      <button id="toMenu" class="btn">Menu</button>
      <button class="btn results-feedback">Feedback</button>
    </div>`;
}

/** Colour a pill by the worst thing it is reporting, so the rail is a glance rather than a read. */
export function updateRail(els: Els, sim: Sim): void {
  const stressed = countStoreys(sim, 0.55);
  const unhappy = sim.tower.units.filter((u) => u.let && u.sat < 0.45).length;
  const broke = sim.cash < 400;
  paint(els.rail.querySelector('[data-ov="lift"]'), stressed > 0, `Lifts${stressed ? ` · ${stressed}` : ''}`);
  paint(els.rail.querySelector('[data-ov="mood"]'), unhappy > 0, `Mood${unhappy ? ` · ${unhappy}` : ''}`);
  paint(els.rail.querySelector('[data-ov="money"]'), broke, 'Money');
}

function paint(el: Element | null, bad: boolean, text: string): void {
  if (!(el instanceof HTMLElement)) return;
  el.textContent = text;
  el.classList.toggle('alert', bad);
}

function countStoreys(sim: Sim, frac: number): number {
  let n = 0;
  for (let s = 0; s < 24; s++) {
    const p = sim.storeyPressure(s);
    if (p.n > 0 && p.worstTicks / 1500 > frac) n++;
  }
  return n;
}

/** Cost and legality of the armed tool at a cell, phrased as what it does to you. */
export function verdictFor(sim: Sim, tool: ToolId, storey: number, col: number): string {
  if (tool === 'sell') return 'Tap a unit to sell it, or give its tenants notice.';
  if (tool === 'car') return 'Tap a riser to add a car.';
  if (tool === 'riser') {
    const v = sim.canBuildRiser(Math.max(8, storey), col);
    return v.ok ? `Cut a riser to storey ${Math.max(8, storey)} · £${(Math.max(8, storey) + 1) * 90}` : v.why;
  }
  if (tool === 'stair') {
    const v = sim.canBuildStair(storey, col);
    return v.ok ? 'Stairs · £90' : v.why;
  }
  const v = sim.canBuildUnit(tool, storey, col);
  return v.ok ? `${UNITS[tool].name} on storey ${storey} · £${UNITS[tool].cost}` : v.why;
}
